import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { AdminReviewsAPI } from '../../Services/ECommerceAPI';
import '../Common/common.css';

const ReviewManagement = () => {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [approvedFilter, setApprovedFilter] = useState('all'); // all | approved | pending
    const [search, setSearch] = useState('');

    const load = async () => {
        try {
            setLoading(true);
            const approved =
                approvedFilter === 'approved' ? true :
                approvedFilter === 'pending' ? false :
                null;
            const data = await AdminReviewsAPI.getAll({ approved });
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            toast.error('Failed to load reviews');
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [approvedFilter]);

    const filtered = useMemo(() => {
        const q = (search || '').trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => {
            const name = (r.reviewerName || r.ReviewerName || '').toLowerCase();
            const email = (r.reviewerEmail || r.ReviewerEmail || '').toLowerCase();
            const product = (r.productName || r.ProductName || '').toLowerCase();
            const title = (r.title || r.Title || '').toLowerCase();
            const comment = (r.comment || r.Comment || '').toLowerCase();
            return [name, email, product, title, comment].some((x) => x.includes(q));
        });
    }, [rows, search]);

    const setApproval = async (id, next) => {
        try {
            await AdminReviewsAPI.setApproval(id, next);
            toast.success(next ? 'Approved' : 'Unapproved');
            setRows((prev) => prev.map((r) => ((r.reviewID ?? r.ReviewID) === id ? { ...r, isApproved: next, IsApproved: next } : r)));
        } catch (e) {
            console.error(e);
            toast.error('Failed to update approval');
        }
    };

    const remove = async (id) => {
        const ok = window.confirm('Delete this review?');
        if (!ok) return;
        try {
            await AdminReviewsAPI.remove(id);
            toast.success('Deleted');
            setRows((prev) => prev.filter((r) => (r.reviewID ?? r.ReviewID) !== id));
        } catch (e) {
            console.error(e);
            toast.error('Failed to delete review');
        }
    };

    return (
        <div className="common-page">
            <div className="common-card">
                <div className="common-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                        <h2 className="common-title" style={{ marginBottom: 4 }}>Review Management</h2>
                        <div className="common-subtitle">Approve or remove product reviews (public reviews show only after approval).</div>
                    </div>
                    <button className="common-button" type="button" onClick={load} disabled={loading}>Refresh</button>
                </div>

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ fontWeight: 600 }}>Status</span>
                        <select className="common-input" value={approvedFilter} onChange={(e) => setApprovedFilter(e.target.value)}>
                            <option value="all">All</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                        </select>
                    </div>
                    <div style={{ flex: 1, minWidth: 240 }}>
                        <input
                            className="common-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by product, name, email, title, comment..."
                        />
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontWeight: 600 }}>
                        {filtered.length} review(s)
                    </div>
                </div>

                <div style={{ marginTop: 12, overflowX: 'auto' }}>
                    <table className="common-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Product</th>
                                <th>Rating</th>
                                <th>Title</th>
                                <th style={{ minWidth: 280 }}>Comment</th>
                                <th>Reviewer</th>
                                <th>Approved</th>
                                <th>Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={9} style={{ padding: 16 }}>Loading…</td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan={9} style={{ padding: 16, color: 'var(--text-muted)' }}>No reviews found.</td></tr>
                            ) : (
                                filtered.map((r) => {
                                    const id = r.reviewID ?? r.ReviewID;
                                    const approved = !!(r.isApproved ?? r.IsApproved);
                                    return (
                                        <tr key={id}>
                                            <td>{id}</td>
                                            <td>{r.productName || r.ProductName}</td>
                                            <td>{r.rating || r.Rating} / 5</td>
                                            <td style={{ maxWidth: 220, wordBreak: 'break-word' }}>{r.title || r.Title || '—'}</td>
                                            <td style={{ maxWidth: 520, wordBreak: 'break-word' }}>{r.comment || r.Comment || '—'}</td>
                                            <td style={{ maxWidth: 220, wordBreak: 'break-word' }}>
                                                <div style={{ fontWeight: 700 }}>{r.reviewerName || r.ReviewerName || 'Anonymous'}</div>
                                                <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{r.reviewerEmail || r.ReviewerEmail || '—'}</div>
                                            </td>
                                            <td>{approved ? 'Yes' : 'No'}</td>
                                            <td>{new Date(r.createdDate || r.CreatedDate).toLocaleDateString()}</td>
                                            <td>
                                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                                    <button
                                                        className="common-button"
                                                        type="button"
                                                        onClick={() => setApproval(id, !approved)}
                                                    >
                                                        {approved ? 'Unapprove' : 'Approve'}
                                                    </button>
                                                    <button
                                                        className="common-button secondary"
                                                        type="button"
                                                        onClick={() => remove(id)}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ReviewManagement;

